import { useState } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { motion, AnimatePresence } from 'motion/react'
import { LuMail, LuSend, LuUser, LuMessageSquare } from 'react-icons/lu'
import { FaWhatsapp } from 'react-icons/fa6'

import { RevealItem, ScrollReveal } from '#/components/common/ScrollReveal'
import { TextAnimate } from '#/components/common/TextAnimate'
import { HighlightText } from '#/components/common/HighlightText'
import { FormInput } from '#/components/common/fields/FormInput'
import { FormPhoneInput } from '#/components/common/fields/FormPhoneInput'
import { FormTextarea } from '#/components/common/fields/FormTextarea'
import headerIcon from '#/assets/header-icon.svg'

import { IslamicGeometricBg, FloatingOrbs } from './ContactSvgs'
import { LucideCheckCircle } from 'lucide-react'
import { useContactMutation } from '../hooks/useContactMutation'

// ─── Types ────────────────────────────────────────────────────────────────────

type ContactFormValues = {
  name: string
  email: string
  phone?: string
  topic: string
  message: string
}

// ─── Contact Info Card ────────────────────────────────────────────────────────

type ContactCardProps = {
  icon: React.ReactNode
  label: string
  value: string
  sub?: string
  href?: string
  accentColor?: string
}

function ContactCard({
  icon,
  label,
  value,
  sub,
  href,
  accentColor = 'rgba(255,182,6,0.12)',
}: ContactCardProps) {
  const inner = (
    <div className="group flex items-start gap-4 rounded-2xl border border-neutral-100 bg-neutral-50/20   p-5  transition-all duration-300 hover:border-primary-500/30 hover:bg-primary-500/10 hover:shadow-[0_10px_40px_rgba(255,182,6,0.1)]">
      <div
        className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-primary-600 text-xl transition-transform duration-300 group-hover:scale-110"
        style={{ background: accentColor }}
      >
        {icon}
      </div>
      <div className="flex flex-col gap-0.5">
        <p className="text-xs font-medium uppercase tracking-widest text-primary-700">
          {label}
        </p>
        <p className="text-base  font-medium ">{value}</p>
        {sub && <p className="text-sm text-neutral-700">{sub}</p>}
      </div>
    </div>
  )

  return href ? (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {inner}
    </a>
  ) : (
    inner
  )
}

// ─── Success Banner ───────────────────────────────────────────────────────────

function SuccessBanner() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="flex flex-col items-center gap-4 rounded-2xl border border-primary-500/30 bg-primary-500/10 px-8 py-10 text-center"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary-500/20 text-primary-400 text-4xl">
        <LucideCheckCircle />
      </div>
      <h3 className="text-2xl font-bold ">Message Sent!</h3>
      <p className="text-base text-neutral-300 max-w-sm">
        JazakAllah khair for reaching out. Our team will respond within 24
        hours, in sha Allah.
      </p>
      <p className="text-sm text-primary-400 font-semibold">بارك الله فيك</p>
    </motion.div>
  )
}

// ─── Contact Form ─────────────────────────────────────────────────────────────

function ContactForm() {
  const [submitted, setSubmitted] = useState(false)
  const contactMutation = useContactMutation()
  const methods = useForm<ContactFormValues>({ defaultValues: { topic: '' } })
  const { handleSubmit } = methods

  const onSubmit = async (data: any) => {
    try {
      await contactMutation.mutateAsync({
        name: data.name,
        email: data.email,
        phone: data.phone,
        subject: data.subject || data.topic,
        message: data.message,
      })
      setSubmitted(true)
    } catch {
      // Handled via customFetch throw
    }
  }

  return (
    <div className="relative overflow-hidden rounded-3xl bg-neutral-50/20 border border-neutral-200 p-8   sm:p-10">
      <AnimatePresence mode="wait">
        {submitted ? (
          <SuccessBanner key="success" />
        ) : (
          <motion.div
            key="form"
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.25 }}
          >
            <div className="mb-6">
              <h3 className="text-3xl font-bold ">Send a Message</h3>
              <p className="mt-1 text-sm text-neutral-400">
                Fill in the form and we'll get back to you shortly.
              </p>
            </div>

            <FormProvider {...methods}>
              <form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                className="flex flex-col gap-5"
              >
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <FormInput
                    name="name"
                    label="Full Name"
                    placeholder="Your full name"
                    icon={<LuUser />}
                    rules={{ required: 'Name is required' }}
                  />
                  <FormInput
                    name="email"
                    label="Email Address"
                    type="email"
                    placeholder="you@example.com"
                    icon={<LuMail />}
                    rules={{
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email',
                      },
                    }}
                  />
                </div>

                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <FormPhoneInput
                    name="phone"
                    label="Phone Number"
                    placeholder="Enter phone number"
                    defaultCountry="EG"
                  />
                  <FormInput
                    name="subject"
                    label="Subject"
                    placeholder="Enter subject"
                    icon={<LuUser />}
                    rules={{ required: 'Subject is required' }}
                  />
                </div>

                <FormTextarea
                  name="message"
                  label="Your Message"
                  placeholder="Write your message here… Assalamu Alaikum!"
                  icon={<LuMessageSquare />}
                  rules={{
                    required: 'Message is required',
                    minLength: {
                      value: 20,
                      message: 'Please write at least 20 characters',
                    },
                  }}
                />

                <motion.button
                  type="submit"
                  disabled={contactMutation.isPending}
                  whileHover={{ scale: contactMutation.isPending ? 1 : 1.02 }}
                  whileTap={{ scale: contactMutation.isPending ? 1 : 0.98 }}
                  className="flex h-13 items-center justify-center gap-3 rounded-xl bg-primary-500 px-8 text-sm font-bold text-neutral-900 transition-colors duration-200 hover:bg-primary-400 hover:shadow-[0_8px_30px_rgba(255,182,6,0.3)] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {contactMutation.isPending ? (
                    <>
                      <span className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-900/40 border-t-neutral-900" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <LuSend />
                      Send Message
                    </>
                  )}
                </motion.button>

                <p className="text-center text-xs text-neutral-500">
                  By submitting, you agree to our Privacy Policy. We never share
                  your data.
                </p>
              </form>
            </FormProvider>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

// ─── Section ──────────────────────────────────────────────────────────────────

export default function ContactFormSection() {
  return (
    <section
      id="contact-form"
      className="relative w-full  bg-background overflow-hidden py-15"
    >
      <IslamicGeometricBg />
      <FloatingOrbs />

      <div className="container relative z-10 mx-auto flex flex-col gap-16">
        {/* Header */}
        <ScrollReveal className="flex flex-col items-center gap-4 text-center">
          <RevealItem>
            <img alt="" src={headerIcon} className="mx-auto" />
          </RevealItem>
          <RevealItem>
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary-600">
              Get In Touch
            </p>
          </RevealItem>
          <RevealItem className="w-full">
            <h2 className="text-4xl font-bold  sm:text-5xl">
              We'd Love to <HighlightText inView text="Hear From You" />
            </h2>
          </RevealItem>
          <RevealItem className="max-w-2xl">
            <TextAnimate
              by="word"
              animation="fadeIn"
              duration={1.1}
              className="text-base text-neutral-400 sm:text-lg"
            >
              Whether you have a question about our courses, need technical
              support, or simply want to say Assalamu Alaikum — our team is here
              for you.
            </TextAnimate>
          </RevealItem>
        </ScrollReveal>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[360px_1fr]">
          {/* Info panel */}
          <ScrollReveal className="flex flex-col gap-5" stagger={0.08}>
            <RevealItem>
              <h3 className="text-3xl font-bold ">Contact Information</h3>
              <p className="mt-1 text-sm text-neutral-400 leading-relaxed">
                Reach us through any of these channels. We respond promptly and
                with care, in sha Allah.
              </p>
            </RevealItem>

            {[
              {
                icon: <FaWhatsapp />,
                label: 'WhatsApp',
                value: '+20 155 066 0071',
                sub: 'Quick responses on WhatsApp',
                href: 'https://wa.me/201550660071',
              },
              {
                icon: <LuMail />,
                label: 'Email',
                value: 'info@wa-allamah.com',
                sub: 'We reply within 24 hours',
                href: 'mailto:info@wa-allamah.com',
              },
            ].map((card) => (
              <RevealItem key={card.label}>
                <ContactCard {...card} />
              </RevealItem>
            ))}
          </ScrollReveal>

          {/* Form panel */}
          <ScrollReveal>
            <RevealItem>
              <ContactForm />
            </RevealItem>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}
